import React from 'react';
import { Star, MapPin, Quote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { mockTestimonials } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TestimonialsPage = () => {
  const { language } = useLanguage();

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-5 h-5">
          <Star className="w-5 h-5 text-yellow-400" />
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
          </div>
        </div>
      );
    }
    
    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-5 h-5 text-gray-300" />);
    }

    return stars;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50">
      <Header />
      
      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-6xl float-animation">⭐</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 bg-clip-text text-transparent">
              {language === 'en' ? 'What Our Members Say' : 'ما يقوله أعضاؤنا'}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'Real stories from real travelers across the Middle East who saved big with NuzhaTrips!' 
                : 'قصص حقيقية من مسافرين حقيقيين عبر الشرق الأوسط الذين وفروا الكثير مع NuzhaTrips!'}
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-2xl font-bold text-gray-800">5.0</span>
              <span className="text-gray-600">
                {language === 'en' ? `(${mockTestimonials.length} reviews)` : `(${mockTestimonials.length} تقييم)`}
              </span>
            </div>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {mockTestimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 card-cartoon relative"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 opacity-10">
                  <Quote className="w-16 h-16 text-orange-500" />
                </div>

                {/* Profile */}
                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover border-4 border-orange-200"
                  />
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">{testimonial.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{testimonial.location}</span>
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Review Text */}
                <p className="text-gray-700 leading-relaxed relative z-10">
                  {testimonial.text[language]}
                </p>

                {/* Verified Badge */}
                <div className="mt-4 inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {language === 'en' ? 'Verified Customer' : 'عميل موثق'}
                </div>
              </div>
            ))}
          </div>

          {/* Stats Section */}
          <div className="bg-white rounded-3xl shadow-xl p-12 mb-16">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              <div className="bounce-light">
                <div className="text-5xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent mb-2">
                  500+
                </div>
                <div className="text-gray-600 font-medium">
                  {language === 'en' ? 'Happy Travelers' : 'مسافر سعيد'}
                </div>
              </div>
              <div className="bounce-light" style={{ animationDelay: '0.2s' }}>
                <div className="text-5xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent mb-2">
                  50K+
                </div>
                <div className="text-gray-600 font-medium">
                  {language === 'en' ? 'OMR Saved' : 'ريال موفر'}
                </div>
              </div>
              <div className="bounce-light" style={{ animationDelay: '0.4s' }}>
                <div className="text-5xl font-bold bg-gradient-to-r from-purple-500 to-orange-500 bg-clip-text text-transparent mb-2">
                  24/7
                </div>
                <div className="text-gray-600 font-medium">
                  {language === 'en' ? 'Support' : 'دعم'}
                </div>
              </div>
              <div className="bounce-light" style={{ animationDelay: '0.6s' }}>
                <div className="text-5xl font-bold bg-gradient-to-r from-orange-500 to-purple-600 bg-clip-text text-transparent mb-2">
                  5.0
                </div>
                <div className="text-gray-600 font-medium">
                  {language === 'en' ? 'Rating' : 'تقييم'}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 rounded-3xl p-12 text-center text-white shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {language === 'en' ? 'Ready to Save on Your Next Trip?' : 'مستعد للتوفير في رحلتك القادمة؟'}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {language === 'en' 
                ? 'Join hundreds of smart travelers who never pay full price for flights!' 
                : 'انضم إلى مئات المسافرين الأذكياء الذين لا يدفعون السعر الكامل للرحلات!'}
            </p>
            <button
              onClick={() => {
                window.scrollTo(0, 0);
                window.location.href = '/#pricing';
              }}
              className="bg-white text-purple-600 font-bold py-4 px-10 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
            >
              {language === 'en' ? 'Start Saving Now' : 'ابدأ التوفير الآن'} ✨
            </button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TestimonialsPage;
